import { Layer, Source } from "@vis.gl/react-maplibre";
import type { MapGeoJSONFeature } from "maplibre-gl";
import { FC, useMemo } from "react";
import useEvent from "react-use-event-hook";

import {
  STATION_DOT_LAST_RADIUS_PX,
  STATION_DOT_RADIUS_PX,
  STATION_DOT_STROKE_WIDTH_PX,
  useMapLibreLayerPointer,
} from "@ryde/controls";
import {
  ILocationInfo,
  IRideRoute,
  isLastRealRouteLocation,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { IRideMapActiveStation } from "../../ride-map-station-popup";

interface IMapLibreStationDotsLayerProps {
  id: string;
  rideId: number;
  rideStatus: Required<IRideRoute>["rideStatus"];
  locations: Required<ILocationInfo>[];
  color: string;
  onOpen: (station: IRideMapActiveStation) => void;
}

/**
 * Stations as GPU circles instead of one HTML `<Marker>` per stop. A crowded live map carries ~48 stops
 * per ride, so a 70-ride selection mounts thousands of markers — and MapLibre repositions every marker
 * individually on each frame, where Leaflet only translated one pane. Used above
 * `CROWDED_MAP_MIN_ROUTES`; below it the pin markers render as before, so single rides and everyday
 * multi-ride selections keep their numbered pins. The raster style ships no glyphs, so numbers cannot be
 * drawn here.
 */
export const MapLibreStationDotsLayer: FC<IMapLibreStationDotsLayerProps> = ({
  id,
  rideId,
  rideStatus,
  locations,
  color,
  onOpen,
}) => {
  const dotsLayerId = `${id}-station-dots`;

  const featureCollection = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: locations.map((locationInfo, waypointIndex) => ({
        type: "Feature" as const,
        properties: {
          waypointIndex,
          isLast: isLastRealRouteLocation({
            locations,
            locationIndex: waypointIndex,
          })
            ? 1
            : 0,
        },
        geometry: {
          type: "Point" as const,
          coordinates: [locationInfo.longitude, locationInfo.latitude] as [
            number,
            number,
          ],
        },
      })),
    }),
    [locations],
  );

  const handleClick = useEvent((feature: MapGeoJSONFeature): void => {
    const waypointIndex = feature.properties?.waypointIndex;

    if (typeof waypointIndex !== "number") {
      return;
    }

    // Passenger details stay in this closure rather than in feature properties — MapLibre would flatten
    // them to strings, and they are only needed for the station that was clicked.
    const locationInfo = locations[waypointIndex];

    if (!locationInfo) {
      return;
    }

    onOpen({
      rideId,
      rideStatus,
      stationId: locationInfo.stationId,
      addressName: locationInfo.addressName ?? "",
      fullAddress: locationInfo.fullAddress ?? "",
      fullPassengersDetails: locationInfo.fullPassengersDetails,
      latitude: locationInfo.latitude,
      longitude: locationInfo.longitude,
      waypointIndex,
    });
  });

  useMapLibreLayerPointer({
    layerId: dotsLayerId,
    enabled: locations.length > 0,
    onClick: handleClick,
  });

  if (!locations.length) {
    return null;
  }

  return (
    <Source id={dotsLayerId} type="geojson" data={featureCollection}>
      <Layer
        id={dotsLayerId}
        type="circle"
        paint={{
          ["circle-radius"]: [
            "case",
            ["==", ["get", "isLast"], 1],
            STATION_DOT_LAST_RADIUS_PX,
            STATION_DOT_RADIUS_PX,
          ],
          ["circle-color"]: color,
          ["circle-stroke-width"]: STATION_DOT_STROKE_WIDTH_PX,
          ["circle-stroke-color"]: rydeColors.white,
        }}
      />
    </Source>
  );
};
